import { uploadResumableChunk } from '@/features/mcap/api/gcsResumable';
import {
  beginResumableUpload,
  completeSubmission,
  initiateMcapSubmission,
  reportSubmissionProgress,
} from '@/features/mcap/api/submissions';
import { MCAP_UPLOAD_CHUNK_BYTES } from '@/features/mcap/config/constants';
import { INITIAL_UPLOAD_STATE } from '@/features/mcap/config/upload';
import { hashFileBlake3 } from '@/features/mcap/lib/blake3Client';
import { hasMcapMagic, makeSubmissionId } from '@/features/mcap/lib/mcapFile';
import { reduceUploadState } from '@/features/mcap/lib/uploadMachine';
import type { SubmissionSession, UploaderSubmissionSession } from '@/features/mcap/types/contracts';
import type { UploadEvent, UploadState } from '@/features/mcap/types/upload';
import { LOCAL_UPLOAD_MAX_AUTO_RETRIES } from '@/features/uploads/config/constants';
import {
  type LocalUploadRetryEvent,
  type LocalUploadRetryOperation,
  isRetryableUploadError,
  runRetryableUploadOperation,
  waitForLocalUploadRetry,
} from '@/features/uploads/lib/uploadRetry';
import { createUploadRelayLease } from '@/features/uploads/services/uploadRelay';
import { withUploadResourceLock } from '@/features/uploads/services/uploadResourceLock';
import {
  type DuplicateAssetConflict,
  duplicateAssetConflictSchema,
} from '@/features/uploads/types/submissionConflicts';
import type { LocalUploadRoute } from '@/features/uploads/types/uploadRelay';
import { ApiHttpError } from '@/shared/api/client';
import { z } from 'zod';
import {
  fetchSubmission,
  fetchUploadOffset,
  fetchUploaderSubmissionPage,
} from './submissionService';

export interface UploadMcapInput {
  uploadRoute?: LocalUploadRoute;
  projectId: string;
  file: File;
  uploaderId?: string;
  /** BLAKE3 computed by the caller's prepared-file pipeline; skips a second worker scan. */
  preparedContentBlake3?: string;
  /** When true, a fresh duplicate selection fails instead of adopting the prior session. */
  rejectDuplicate?: boolean;
  signal?: AbortSignal;
  /** A paused session created by this form instance. It is validated before any write is resumed. */
  resumeSessionId?: string;
  onState?: (state: UploadState) => void;
  onRetry?: (event: LocalUploadRetryEvent) => void;
}

export interface UploadMcapResult {
  sessionId: string;
  projectId: string;
}

interface ExpectedSubmission {
  projectId: string;
  contentBlake3: string;
  declaredBytes: string;
  mediaType: 'mcap';
  originalFilename: string;
}

type McapUploadOperation = 'validate_file' | 'hash_file' | LocalUploadRetryOperation;

interface RetryControls {
  relay?: ReturnType<typeof createUploadRelayLease>;
  signal?: AbortSignal;
  onRetry?: (event: LocalUploadRetryEvent) => void;
}

class RetryableMcapUploadError extends Error {
  readonly transport: 'browser_file' | 'gcs_resumable_fetch' | 'vision_lab_api';

  constructor(
    readonly operation: McapUploadOperation,
    cause: unknown,
  ) {
    super('network_error', { cause });
    this.name = 'RetryableMcapUploadError';
    this.transport =
      operation === 'upload_chunk'
        ? 'gcs_resumable_fetch'
        : operation === 'validate_file' || operation === 'hash_file'
          ? 'browser_file'
          : 'vision_lab_api';
  }
}

/** A current-user pending/uploading conflict that the engine can continue with the retained File. */
export class McapResumeAvailableError extends Error {
  constructor(
    readonly session: SubmissionSession,
    readonly contentBlake3: string,
  ) {
    super('mcap_resume_available');
    this.name = 'McapResumeAvailableError';
  }
}

/** Exact content that has already become an immutable asset. Unlike an owned submission conflict,
 * this is terminal for upload and must never trigger a session lookup or resumable write. */
export class McapDuplicateAssetError extends Error {
  constructor(readonly conflict: DuplicateAssetConflict) {
    super('duplicate_asset');
    this.name = 'McapDuplicateAssetError';
  }
}

function assertResumeSessionMatches(
  session: SubmissionSession | UploaderSubmissionSession,
  expected: ExpectedSubmission,
  mismatchCode = 'resume_session_mismatch',
): void {
  if (
    session.projectId !== expected.projectId ||
    session.contentBlake3 !== expected.contentBlake3 ||
    session.declaredBytes !== expected.declaredBytes ||
    session.mediaType !== expected.mediaType
  ) {
    throw new Error(mismatchCode);
  }
  if (session.status === 'failed') throw new Error('resume_session_failed');
  if (session.status === 'abandoned') throw new Error('resume_session_abandoned');
}

function isConflictError(error: unknown): error is ApiHttpError {
  return error instanceof ApiHttpError && error.status === 409;
}

function isDuplicateSubmissionError(error: unknown): error is ApiHttpError {
  return isConflictError(error) && error.serverMessage === 'duplicate_submission';
}

function duplicateAssetConflict(error: unknown): DuplicateAssetConflict | undefined {
  if (!isConflictError(error) || error.serverMessage !== 'duplicate_asset') return undefined;
  const parsed = duplicateAssetConflictSchema.safeParse(error.payload);
  return parsed.success ? parsed.data : undefined;
}

const ownedConflictSchema = z
  .object({
    conflict: z.object({ sessionId: z.string().min(1) }).strict(),
  })
  .passthrough();

function retryTransport(operation: LocalUploadRetryOperation) {
  return operation === 'upload_chunk'
    ? ('gcs_resumable_fetch' as const)
    : ('vision_lab_api' as const);
}

function runMcapRetryableOperation<T>(
  operation: LocalUploadRetryOperation,
  controls: RetryControls,
  run: () => Promise<T>,
): Promise<T> {
  return runRetryableUploadOperation({
    operation,
    transport: retryTransport(operation),
    run,
    signal: controls.signal,
    onRetry: controls.onRetry,
  });
}

async function fetchSubmissionIfExists(
  sessionId: string,
  controls: RetryControls,
): Promise<SubmissionSession | undefined> {
  try {
    return await runMcapRetryableOperation('resolve_submission', controls, () =>
      fetchSubmission(sessionId),
    );
  } catch (error) {
    if (error instanceof ApiHttpError && error.status === 404) return undefined;
    throw error;
  }
}

async function findExistingSubmission(
  uploaderId: string,
  contentBlake3: string,
  controls: RetryControls,
): Promise<UploaderSubmissionSession | undefined> {
  let cursor: string | undefined;
  const seenCursors = new Set<string>();
  for (;;) {
    const page = await runMcapRetryableOperation('resolve_submission', controls, () =>
      fetchUploaderSubmissionPage(uploaderId, { cursor }),
    );
    const match = page.items.find((session) => session.contentBlake3 === contentBlake3);
    if (match) return match;
    if (!page.nextCursor) return undefined;
    if (seenCursors.has(page.nextCursor)) throw new Error('duplicate_session_lookup_invalid');
    seenCursors.add(page.nextCursor);
    cursor = page.nextCursor;
  }
}

async function currentUploaderId(): Promise<string | undefined> {
  const { getCurrentAuthUser } = await import('@/shared/lib/authSession');
  return getCurrentAuthUser()?.uid;
}

async function resolveSubmission(
  attemptedSessionId: string,
  resumeSessionId: string | undefined,
  uploaderId: string | undefined,
  rejectDuplicate: boolean,
  expected: ExpectedSubmission,
  controls: RetryControls,
): Promise<SubmissionSession | UploaderSubmissionSession> {
  if (resumeSessionId) {
    const existing = await runMcapRetryableOperation('resolve_submission', controls, () =>
      fetchSubmission(resumeSessionId),
    );
    assertResumeSessionMatches(existing, expected);
    return existing;
  }

  for (let retries = 0; ; retries += 1) {
    try {
      return await initiateMcapSubmission({ sessionId: attemptedSessionId, ...expected });
    } catch (error) {
      const assetConflict = duplicateAssetConflict(error);
      if (assetConflict) throw new McapDuplicateAssetError(assetConflict);
      if (isDuplicateSubmissionError(error)) {
        const parsedConflict = ownedConflictSchema.safeParse(error.payload);
        const conflictSessionId = parsedConflict.success
          ? parsedConflict.data.conflict.sessionId
          : undefined;
        if (conflictSessionId) {
          const conflict = await runMcapRetryableOperation('resolve_submission', controls, () =>
            fetchSubmission(conflictSessionId),
          );
          assertResumeSessionMatches(conflict, expected, 'duplicate_session_conflict');
          if (conflict.status === 'pending' || conflict.status === 'uploading') {
            throw new McapResumeAvailableError(conflict, expected.contentBlake3);
          }
          return conflict;
        }
        const attempted = await fetchSubmissionIfExists(attemptedSessionId, controls);
        if (attempted) {
          assertResumeSessionMatches(attempted, expected);
          return attempted;
        }
        if (rejectDuplicate) throw new Error('duplicate_submission', { cause: error });
        const ownerId = uploaderId ?? (await currentUploaderId());
        if (!ownerId) throw new Error('duplicate_session_lookup_unavailable', { cause: error });
        let existing: UploaderSubmissionSession | undefined;
        try {
          existing = await findExistingSubmission(ownerId, expected.contentBlake3, controls);
        } catch (lookupError) {
          throw new Error('duplicate_session_lookup_failed', { cause: lookupError });
        }
        if (!existing) throw new Error('duplicate_session_unresolvable', { cause: error });
        assertResumeSessionMatches(existing, expected, 'duplicate_session_conflict');
        return existing;
      }
      if (!isRetryableUploadError(error) || retries >= LOCAL_UPLOAD_MAX_AUTO_RETRIES) throw error;
      const attempted = await fetchSubmissionIfExists(attemptedSessionId, controls);
      if (attempted) {
        assertResumeSessionMatches(attempted, expected);
        return attempted;
      }
      await waitForLocalUploadRetry({
        operation: 'resolve_submission',
        transport: 'vision_lab_api',
        attempt: retries + 1,
        error,
        signal: controls.signal,
        onRetry: controls.onRetry,
      });
    }
  }
}

async function beginResolvedSubmission(
  sessionId: string,
  expected: ExpectedSubmission,
  controls: RetryControls,
): Promise<SubmissionSession> {
  try {
    return await runMcapRetryableOperation('begin_resumable', controls, () =>
      beginResumableUpload(sessionId),
    );
  } catch (error) {
    if (!isConflictError(error)) throw error;
    const latest = await runMcapRetryableOperation('begin_resumable', controls, () =>
      fetchSubmission(sessionId),
    );
    assertResumeSessionMatches(latest, expected);
    if (latest.status === 'uploaded' || latest.status === 'failed') return latest;
    return runMcapRetryableOperation('begin_resumable', controls, () =>
      beginResumableUpload(sessionId),
    );
  }
}

async function reportProgressWithRetry(
  sessionId: string,
  partNo: number,
  cumulativeBytes: bigint,
  controls: RetryControls,
): Promise<SubmissionSession> {
  for (let retries = 0; ; retries += 1) {
    try {
      return await reportSubmissionProgress(sessionId, partNo, cumulativeBytes.toString());
    } catch (error) {
      if (!isRetryableUploadError(error)) throw error;
      const latest = await runMcapRetryableOperation('report_progress', controls, () =>
        fetchSubmission(sessionId),
      );
      const received = BigInt(latest.receivedBytes);
      if (latest.status === 'failed') throw new Error('resume_session_failed', { cause: error });
      if (received === cumulativeBytes && latest.nextPart === partNo + 1) return latest;
      if (
        latest.status !== 'uploading' ||
        latest.nextPart !== partNo ||
        received > cumulativeBytes
      ) {
        throw new Error('upload_progress_conflict', { cause: error });
      }
      if (retries >= LOCAL_UPLOAD_MAX_AUTO_RETRIES) throw error;
      await waitForLocalUploadRetry({
        operation: 'report_progress',
        transport: 'vision_lab_api',
        attempt: retries + 1,
        error,
        signal: controls.signal,
        onRetry: controls.onRetry,
      });
    }
  }
}

async function uploadChunkWithRetry(
  sessionId: string,
  uploadUri: string,
  file: File,
  start: bigint,
  endExclusive: bigint,
  controls: RetryControls,
): Promise<bigint> {
  const total = BigInt(file.size);
  for (let retries = 0; ; retries += 1) {
    try {
      const destination = controls.relay
        ? await controls.relay.destination(sessionId, uploadUri, controls.signal)
        : { url: uploadUri, headers: undefined };
      return await uploadResumableChunk({
        uploadUri: destination.url,
        headers: destination.headers,
        file,
        start,
        endExclusive,
        signal: controls.signal,
      });
    } catch (error) {
      if (
        controls.relay &&
        retries === 0 &&
        typeof error === 'object' &&
        error !== null &&
        'status' in error &&
        error.status === 401
      ) {
        controls.relay.invalidate();
        continue;
      }
      if (!isRetryableUploadError(error) || retries >= LOCAL_UPLOAD_MAX_AUTO_RETRIES) throw error;
      const offset = BigInt(
        await runMcapRetryableOperation('fetch_upload_offset', controls, () =>
          fetchUploadOffset(sessionId, { signal: controls.signal }),
        ),
      );
      if (offset < start) throw new Error('upload_offset_regressed', { cause: error });
      if (offset > total) throw new Error('upload_offset_overflow', { cause: error });
      if (offset > start) return offset;
      await waitForLocalUploadRetry({
        operation: 'upload_chunk',
        transport: 'gcs_resumable_fetch',
        attempt: retries + 1,
        error,
        signal: controls.signal,
        onRetry: controls.onRetry,
      });
    }
  }
}

async function completeWithRetry(
  sessionId: string,
  controls: RetryControls,
): Promise<SubmissionSession> {
  for (let retries = 0; ; retries += 1) {
    try {
      return await completeSubmission(sessionId);
    } catch (error) {
      if (!isRetryableUploadError(error) && !isDuplicateSubmissionError(error)) throw error;
      const latest = await runMcapRetryableOperation('complete_submission', controls, () =>
        fetchSubmission(sessionId),
      );
      if (latest.status === 'uploaded') return latest;
      if (latest.status === 'failed') throw new Error('resume_session_failed', { cause: error });
      if (!isRetryableUploadError(error)) throw error;
      if (retries >= LOCAL_UPLOAD_MAX_AUTO_RETRIES) throw error;
      await waitForLocalUploadRetry({
        operation: 'complete_submission',
        transport: 'vision_lab_api',
        attempt: retries + 1,
        error,
        signal: controls.signal,
        onRetry: controls.onRetry,
      });
    }
  }
}

export async function uploadMcapFile({
  uploadRoute,
  projectId,
  file,
  uploaderId,
  rejectDuplicate = false,
  preparedContentBlake3,
  signal,
  resumeSessionId,
  onState,
  onRetry,
}: UploadMcapInput): Promise<UploadMcapResult> {
  let state = INITIAL_UPLOAD_STATE;
  let operation: McapUploadOperation = 'validate_file';
  const dispatch = (event: UploadEvent) => {
    state = reduceUploadState(state, event);
    onState?.(state);
  };
  const controls: RetryControls = {
    signal,
    onRetry,
    ...(uploadRoute === 'china' ? { relay: createUploadRelayLease('china') } : {}),
  };

  try {
    if (!(await hasMcapMagic(file))) throw new Error('invalid_mcap_magic');
    dispatch({ type: 'file_selected', totalBytes: BigInt(file.size) });
    operation = 'hash_file';
    const contentBlake3 =
      preparedContentBlake3 ??
      (await hashFileBlake3(
        file,
        ({ completedBytes, totalBytes }) =>
          dispatch({ type: 'hash_progress', progress: completedBytes / totalBytes }),
        signal,
      ));
    dispatch({ type: 'hash_complete' });

    return await withUploadResourceLock(
      { uploaderId: uploaderId || 'current-session', contentBlake3 },
      signal,
      async () => {
        // Resolve only after acquiring the resource. Another tab may have completed while this one
        // was hashing or waiting, in which case the uploaded session is returned without a second
        // resumable write or complete request.
        const attemptedSessionId = resumeSessionId ?? makeSubmissionId();
        dispatch({ type: 'initiate', sessionId: attemptedSessionId });
        const expected: ExpectedSubmission = {
          projectId,
          contentBlake3,
          declaredBytes: String(file.size),
          mediaType: 'mcap',
          originalFilename: file.name,
        };
        operation = 'resolve_submission';
        const session = await resolveSubmission(
          attemptedSessionId,
          resumeSessionId,
          uploaderId,
          rejectDuplicate,
          expected,
          controls,
        );
        const sessionId = session.sessionId;
        dispatch({ type: 'session_resolved', sessionId });
        if (session.status === 'uploaded') {
          dispatch({ type: 'processing' });
          return { sessionId, projectId };
        }
        dispatch({ type: 'session_created' });

        operation = 'begin_resumable';
        const resumable = await beginResolvedSubmission(sessionId, expected, controls);
        if (resumable.status === 'uploaded') {
          dispatch({ type: 'processing' });
          return { sessionId, projectId };
        }
        if (resumable.status === 'failed') throw new Error('resume_session_failed');
        if (resumable.status !== 'uploading') throw new Error('invalid_resumable_session_state');
        if (!resumable.resumableUri) throw new Error('resumable_uri_unavailable');
        const reportedBytes = BigInt(resumable.receivedBytes);
        let partNo = resumable.nextPart;
        const total = BigInt(file.size);
        operation = 'fetch_upload_offset';
        let committed = BigInt(
          await runMcapRetryableOperation('fetch_upload_offset', controls, () =>
            fetchUploadOffset(sessionId, { signal }),
          ),
        );
        if (committed > total) throw new Error('upload_offset_overflow');
        if (committed < reportedBytes) throw new Error('upload_offset_regressed');
        if (committed > reportedBytes) {
          operation = 'report_progress';
          const reported = await reportProgressWithRetry(sessionId, partNo, committed, controls);
          partNo = reported.nextPart;
        }
        dispatch({ type: 'resumable_opened', completedBytes: committed });

        while (committed < total) {
          const end = committed + BigInt(MCAP_UPLOAD_CHUNK_BYTES);
          const endExclusive = end < total ? end : total;
          operation = 'upload_chunk';
          const confirmed = await uploadChunkWithRetry(
            sessionId,
            resumable.resumableUri,
            file,
            committed,
            endExclusive,
            controls,
          );
          if (confirmed <= committed) throw new Error('upload_made_no_progress');
          committed = confirmed;
          operation = 'report_progress';
          const reported = await reportProgressWithRetry(sessionId, partNo, committed, controls);
          partNo = reported.nextPart;
          dispatch({ type: 'chunk_committed', completedBytes: committed });
        }

        dispatch({ type: 'finalize' });
        operation = 'complete_submission';
        await completeWithRetry(sessionId, controls);
        dispatch({ type: 'processing' });
        return { sessionId, projectId };
      },
    );
  } catch (error) {
    if (error instanceof DOMException && error.name === 'AbortError') {
      dispatch({ type: 'cancel' });
      throw new Error('upload_canceled', { cause: error });
    }
    if (error instanceof McapResumeAvailableError) throw error;
    if (state.sessionId && error instanceof Error && error.message === 'resume_session_mismatch') {
      dispatch({ type: 'retryable_failure', code: 'resume_session_mismatch' });
      throw error;
    }
    if (state.sessionId && state.phase === 'opening_resumable' && isConflictError(error)) {
      dispatch({ type: 'retryable_failure', code: 'resumable_begin_conflict' });
      throw new Error('resumable_begin_conflict', { cause: error });
    }
    if (isRetryableUploadError(error)) {
      dispatch({ type: 'retryable_failure', code: 'network_error' });
      throw new RetryableMcapUploadError(operation, error);
    }
    dispatch({
      type: 'fatal_failure',
      code: error instanceof Error ? error.message : 'upload_failed',
    });
    throw error;
  }
}
